interface SkiAreaData {
  [skiAreaName: string]: {
    metadata: {
      base_elevation: number;
      top_elevation: number;
      latitude: number;
      longitude: number;
      name: string;
      timezone: string;
    };
    stations: {
      [stationId: string]: {
        metadata: {
          distance_to_ski_area: number;
          latitude: number;
          longitude: number;
          elevation: number;
          stationName: string;
        };
        timestamps: string[];
        source: Array<'history' | 'prediction'>;
        sensors: {
          [sensorName: string]: Array<number | null>;
        };
      };
    };
    ski_area: {
      timestamps: string[];
      source?: string[];
      values: {
        base_temperature: Array<number | null>;
        summit_temperature: Array<number | null>;
        freezing_level: Array<number | null>;
        snow_level: Array<number | null>;
        snow_hourly_base: Array<number | null>;
        snow_hourly_summit: Array<number | null>;
        swe: Array<number | null>;
        wind_dir_summit: Array<number | null>;
        wind_gust_summit: Array<number | null>;
        wind_speed_summit: Array<number | null>;
        cloud_cover: Array<number | null>;
      };
    };
  };
}

export interface FlattenedRow {
  id: string;
  station_id: string;
  distance_to_ski_area: number;
  elevation: number;
  stationName: string;
  sensor_name: string;
  sensor_display_name: string;
  [key: string]: string | number | null; // For dynamic timestamp columns
}

interface TimestampColumn {
  field: string;
  headerName: string;
  width: number;
  type: string;
  description?: string;
}

// Define sensor display names
const sensorDisplayNames: { [key: string]: string } = {
  // Ski area calculated values
  "base_temperature": "Base Temperature",
  "summit_temperature": "Summit Temperature",
  "freezing_level": "Freezing Level",
  "snow_hourly_base": "Snow Rate (Base)",
  "snow_hourly_summit": "Snow Rate (Summit)",
  "swe": "Snow Water Equivalent",
  "wind_dir_summit": "Wind Direction (Summit)",
  "wind_gust_summit": "Wind Gust (Summit)",
  "wind_speed_summit": "Wind Speed (Summit)",
  "cloud_cover": "Cloud Cover",
  // Station sensors
  "temperature": "Temperature",
  "precipRate": "Precipitation",
  "windSpeed": "Wind Speed",
  "windGust": "Wind Gust Speed",
  "windDir": "Wind Direction",
  "relHumidity": "Relative Humidity",
  "roadTemperature1": "Road Surface Temperature (1)",
  "roadTemperature2": "Road Surface Temperature (2)",
  "roadLiquidFreezeTemp1": "Road Liquid Freezing Temperature (1)",
  "roadLiquidFreezeTemp2": "Road Liquid Freezing Temperature (2)",
  "dewpoint": "Dew Point",
  "stationPressure": "Station Pressure",
  "visibility": "Visibility",
  "solarRadiation": "Solar Radiation Intensity"
};

export const formatValue = (value: number | null | undefined, isElevation = false, isPrecipRate = false, isSnowRate = false): string => {
  if (value == null) return '';  // This checks for both null and undefined
  
  if (isElevation) {
    return `${Math.round(value)}'`;
  }
  
  if (isPrecipRate) {
    if (isSnowRate) {
      const fixed = value.toFixed(1);
      return value < 1 ? fixed.replace('0', '') : fixed;
    } else {
      const fixed = value.toFixed(2);
      return value < 1 ? fixed.replace('0', '') : fixed;
    }
  }
  
  return Math.round(value).toString();
};

export const transformStationData = (
  skiArea: SkiAreaData[string], 
  includeSkiAreaValues: boolean = true
): {
  rows: FlattenedRow[];
  timestampColumns: TimestampColumn[];
} => {
  // Get timestamps from first station for station data
  const firstStationId = Object.keys(skiArea.stations)[0];
  const stationTimestamps = skiArea.stations[firstStationId].timestamps;
  const skiAreaTimestamps = skiArea.ski_area.timestamps;

  // Use station timestamps for columns
  const timestampColumns = stationTimestamps.map(timestamp => {
    const date = new Date(timestamp);
    const hour = date.getHours(); // Using local time
    const ampm = hour >= 12 ? 'pm' : 'am';
    const hour12 = hour % 12 || 12;
    const formattedHour = `${hour12}${ampm}`;

    return {
      field: `timestamp_${timestamp}`,
      headerName: formattedHour,
      width: 70,
      type: 'number',
      description: timestamp // Full timestamp in tooltip (already in local time)
    };
  });

  // Create flattened rows
  const rows: FlattenedRow[] = [];

  // Add station sensor rows
  Object.entries(skiArea.stations).forEach(([stationId, stationInfo]) => {
    Object.entries(stationInfo.sensors).forEach(([sensorName, values]) => {
      // Create base row with station and sensor info
      const baseRow: FlattenedRow = {
        id: `${stationId}_${sensorName}`,
        station_id: stationId,
        distance_to_ski_area: stationInfo.metadata.distance_to_ski_area,
        elevation: stationInfo.metadata.elevation,
        stationName: stationInfo.metadata.stationName,
        sensor_name: sensorName,
        sensor_display_name: sensorDisplayNames[sensorName] || sensorName,
      };

      // Add timestamp values using station timestamps
      stationTimestamps.forEach((timestamp, index) => {
        baseRow[`timestamp_${timestamp}`] = values[index];
      });

      rows.push(baseRow);
    });
  });

  // Add ski area calculated values if includeSkiAreaValues is true
  if (includeSkiAreaValues) {
    Object.entries(skiArea.ski_area.values)
      .filter(([valueName]) => valueName !== 'snow_level')
      .forEach(([valueName, values]) => {
      const baseRow: FlattenedRow = {
        id: `ski_area_${valueName}`,
        station_id: 'ski_area',
        distance_to_ski_area: 0,
        elevation: skiArea.metadata.top_elevation,
        stationName: 'Ski Area Calculated',
        sensor_name: valueName,
        sensor_display_name: sensorDisplayNames[valueName] || valueName,
      };

      // Add timestamp values using ski area timestamps that match station timestamps
      stationTimestamps.forEach((stationTimestamp) => {
        const skiAreaIndex = skiAreaTimestamps.indexOf(stationTimestamp);
        baseRow[`timestamp_${stationTimestamp}`] = skiAreaIndex !== -1 ? values[skiAreaIndex] : null;
      });

      rows.push(baseRow);
    });
  }

  // Define sensor order
  const sensorOrder = [
    // Ski area calculated values
    'base_temperature',
    'summit_temperature',
    'freezing_level',
    'snow_hourly_base',
    'snow_hourly_summit',
    'swe',
    'wind_speed_summit',
    'wind_gust_summit',
    'wind_dir_summit',
    'cloud_cover',
    // Station sensors
    'temperature',
    'precipRate',
    'windSpeed',
    'windGust',
    'windDir',
    'relHumidity',
    'roadTemperature1',
    'roadTemperature2',
    'roadLiquidFreezeTemp1',
    'roadLiquidFreezeTemp2',
    'dewpoint',
    'stationPressure',
    'visibility',
    'solarRadiation'
  ];

  // Create a map for quick index lookup
  const sensorOrderMap = new Map(sensorOrder.map((name, index) => [name, index]));

  // Sort rows by station type (ski area first), then sensor order, then elevation
  rows.sort((a, b) => {
    // First sort by station type (ski area calculated values first)
    if (a.station_id === 'ski_area' && b.station_id !== 'ski_area') return -1;
    if (a.station_id !== 'ski_area' && b.station_id === 'ski_area') return 1;
    
    // Then sort by sensor order
    const indexA = sensorOrderMap.get(a.sensor_name as string) ?? Number.MAX_SAFE_INTEGER;
    const indexB = sensorOrderMap.get(b.sensor_name as string) ?? Number.MAX_SAFE_INTEGER;
    if (indexA !== indexB) return indexA - indexB;
    
    // Finally sort by elevation in descending order for same sensor
    return b.elevation - a.elevation;
  });

  return { rows, timestampColumns };
};
